import { Trash2, UserPlus } from 'lucide-react';
import { useId, useRef, useState } from 'react';
import { nextAIReset, resetPhraseWithUTC } from '../../shared/aiAllowance';
import type { AdminView, MemberView } from '../../shared/api';
import { browserTimeZone, diffDays, monthDay, todayIn, type ISODate } from '../../shared/dates';
import { useAdmin, useInvite, useRemoveMember, useRevokeInvite, useSession, useSetAiLimit, useSettings } from '../api/hooks';
import { isReauthRequired, ReauthPrompt } from '../components/ReauthPrompt';
import { useUI } from '../lib/ui';

const MAX_AI_LIMIT = 10_000;

/** A server timestamp as a calendar date in this browser's time zone. */
function localDate(iso: string): ISODate | null {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? todayIn(browserTimeZone(), new Date(t)) : null;
}

function dayLabel(date: ISODate, today: ISODate): string {
  return date.slice(0, 4) === today.slice(0, 4) ? monthDay(date) : `${monthDay(date)}, ${date.slice(0, 4)}`;
}

function dateLabel(iso: string, today: ISODate): string {
  const date = localDate(iso);
  return date ? dayLabel(date, today) : '—';
}

function lastSeenLabel(iso: string | null, today: ISODate): string {
  if (!iso) return 'Never';
  const date = localDate(iso);
  if (!date) return '—';
  const ago = diffDays(today, date);
  if (ago <= 0) return 'Today';
  if (ago === 1) return 'Yesterday';
  if (ago < 7) return `${ago} days ago`;
  return dayLabel(date, today);
}

/** What to call someone here: their name, else their first address. */
function memberLabel(m: MemberView): string {
  return m.name.trim() || m.emails[0] || 'Unnamed account';
}

/** Today's allowance; null = no personal cap (admins, unless one was set). */
function effectiveLimit(m: MemberView, defaultAiLimit: number): number | null {
  return m.aiDailyLimit ?? (m.role === 'admin' ? null : defaultAiLimit);
}

function InviteSection({ view, google, emailCodes }: { view: AdminView; google: boolean; emailCodes: boolean }) {
  const { today, toast } = useUI();
  const invite = useInvite();
  const revoke = useRevokeInvite();
  const [email, setEmail] = useState('');
  // Inviting needs a recent sign-in.
  const [reauth, setReauth] = useState<string | null>(null);
  const how = google && emailCodes ? 'using Google or an emailed code' : google ? 'using Google' : 'using an emailed code';

  return (
    <section className="card card--pad stack" style={{ gap: 14 }}>
      <div>
        <h2 className="section-title">Invite someone</h2>
        <p className="small muted" style={{ marginTop: 4 }}>
          They sign in with this address, {how}, and get their own separate log.
        </p>
      </div>
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault();
          const to = email.trim();
          if (!to) return;
          invite.mutate(to, {
            onSuccess: () => {
              setReauth(null);
              toast(emailCodes ? `Invited ${to}. An invite email is on its way.` : `Invited ${to}. Let them know they can sign in.`);
              setEmail('');
            },
            onError: (err) => (isReauthRequired(err) ? setReauth(err.message) : toast(err.message, { tone: 'error' })),
          });
        }}
      >
        <input
          className="input grow"
          type="email"
          inputMode="email"
          autoComplete="off"
          required
          placeholder="name@example.com"
          aria-label="Email address to invite"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <button className="btn btn--primary" disabled={!email.trim() || invite.isPending}>
          <UserPlus size={17} /> Invite
        </button>
      </form>
      {reauth && <ReauthPrompt message={reauth} />}

      <div className="stack" style={{ gap: 2 }}>
        <div className="ngroup__title">Pending invites · {view.invites.length}</div>
        {view.invites.length === 0 ? (
          <p className="small muted">No pending invites. An invite is used up once that person signs in.</p>
        ) : (
          <div className="list">
            {view.invites.map((inv) => (
              <div key={inv.email} className="list-item">
                <div className="grow">
                  <div className="ellipsis" style={{ fontWeight: 600 }}>
                    {inv.email}
                  </div>
                  <div className="tiny muted">Invited {dateLabel(inv.createdAt, today)}</div>
                </div>
                <button
                  className="btn btn--quiet btn--sm"
                  disabled={revoke.isPending}
                  aria-label={`Revoke the invite for ${inv.email}`}
                  onClick={() =>
                    revoke.mutate(inv.email, {
                      onSuccess: () => toast(`Invite for ${inv.email} revoked`),
                      onError: (err) => toast(err.message, { tone: 'error' }),
                    })
                  }
                >
                  Revoke
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/** Per-person daily AI limit. Empty means the default (members) or no cap (admins). Keyed by the saved value, so a save resets it. */
function AiLimitForm({ member: m, label, defaultAiLimit }: { member: MemberView; label: string; defaultAiLimit: number }) {
  const { toast } = useUI();
  const setLimit = useSetAiLimit();
  const [text, setText] = useState(m.aiDailyLimit === null ? '' : String(m.aiDailyLimit));
  // Changing an allowance needs a recent sign-in.
  const [reauth, setReauth] = useState<string | null>(null);
  const trimmed = text.trim();
  // undefined = not a valid limit
  const value = trimmed === '' ? null : /^\d{1,5}$/.test(trimmed) && Number(trimmed) <= MAX_AI_LIMIT ? Number(trimmed) : undefined;
  const dirty = value !== undefined && value !== m.aiDailyLimit;
  const id = `ai-limit-${m.id}`;

  return (
    <form
      className="stack"
      style={{ gap: 6 }}
      onSubmit={(e) => {
        e.preventDefault();
        if (value === undefined || !dirty) return;
        setLimit.mutate(
          { id: m.id, aiDailyLimit: value },
          {
            onSuccess: () => {
              setReauth(null);
              toast(
                value !== null
                  ? `${label}: ${value} AI ${value === 1 ? 'request' : 'requests'} a day`
                  : m.role === 'admin'
                    ? `${label}: no personal AI cap`
                    : `${label}: back to the default, ${defaultAiLimit} a day`,
              );
            },
            onError: (err) => (isReauthRequired(err) ? setReauth(err.message) : toast(err.message, { tone: 'error' })),
          },
        );
      }}
    >
      <label className="field__label" htmlFor={id}>
        Daily AI limit<span className="sr-only"> for {label}</span>
      </label>
      <div className="row">
        <input
          id={id}
          className="input input--sm num"
          style={{ width: 132 }}
          inputMode="numeric"
          placeholder={m.role === 'admin' ? 'No cap' : `Default (${defaultAiLimit})`}
          value={text}
          aria-invalid={value === undefined}
          aria-describedby={value === undefined ? `${id}-error` : undefined}
          onChange={(e) => setText(e.target.value)}
        />
        <button className="btn btn--ghost btn--sm" disabled={!dirty || setLimit.isPending}>
          Save<span className="sr-only"> the AI limit for {label}</span>
        </button>
      </div>
      {value === undefined && (
        <span id={`${id}-error`} className="field__error">
          Use a whole number up to {MAX_AI_LIMIT.toLocaleString('en-US')}, or leave it empty.
        </span>
      )}
      {reauth && <ReauthPrompt message={reauth} />}
    </form>
  );
}

function MemberRow({ member: m, defaultAiLimit, isMe }: { member: MemberView; defaultAiLimit: number; isMe: boolean }) {
  const { today, toast } = useUI();
  const remove = useRemoveMember();
  const [confirming, setConfirming] = useState(false);
  // Removing someone needs a recent sign-in.
  const [reauth, setReauth] = useState<string | null>(null);
  const removeRef = useRef<HTMLButtonElement>(null);
  const promptId = useId();
  const cancel = () => {
    setConfirming(false);
    setReauth(null);
    // Give focus back to the Remove button the confirm replaced.
    requestAnimationFrame(() => removeRef.current?.focus());
  };
  const label = memberLabel(m);
  const limit = effectiveLimit(m, defaultAiLimit);
  // The label already shows the first address when there's no name.
  const moreEmails = m.name.trim() ? m.emails : m.emails.slice(1);

  return (
    <div className="member">
      <div className="stack" style={{ gap: 2 }}>
        <div className="row" style={{ gap: 6 }}>
          <span className="ellipsis" style={{ fontWeight: 650 }}>
            {label}
          </span>
          {m.role === 'admin' && <span className="badge badge--ai">Admin</span>}
          {isMe && <span className="badge">You</span>}
        </div>
        {moreEmails.map((email) => (
          <div key={email} className="small muted ellipsis">
            {email}
          </div>
        ))}
      </div>

      <dl className="member__facts">
        <div>
          <dt>Joined</dt>
          <dd>{dateLabel(m.createdAt, today)}</dd>
        </div>
        <div>
          <dt>Last seen</dt>
          <dd>{lastSeenLabel(m.lastSeenAt, today)}</dd>
        </div>
        <div>
          <dt>Set up</dt>
          <dd>{m.onboarded ? 'Yes' : 'Not yet'}</dd>
        </div>
        <div>
          <dt>AI today</dt>
          <dd className="num">{limit === null ? `${m.aiCallsToday} · no cap` : `${m.aiCallsToday} of ${limit}`}</dd>
        </div>
      </dl>

      <div className="row wrap between" style={{ alignItems: 'flex-end' }}>
        <AiLimitForm key={String(m.aiDailyLimit)} member={m} label={label} defaultAiLimit={defaultAiLimit} />
        {/* Admin accounts come from ADMIN_EMAILS in the server config; they can't be removed here. */}
        {!isMe && m.role !== 'admin' && !confirming && (
          <button ref={removeRef} className="btn btn--quiet btn--sm" aria-label={`Remove ${label}`} onClick={() => setConfirming(true)}>
            <Trash2 size={15} /> Remove
          </button>
        )}
      </div>

      {confirming && (
        <div className="card card--soft stack" style={{ padding: 12, gap: 10 }} role="group" aria-labelledby={promptId}>
          <p id={promptId} className="small">
            Remove <b>{label}</b>? This deletes their account and everything in it: log, weigh-ins, foods, recipes and Coach chats. Server backups keep a
            copy for up to 7 days.
          </p>
          <div className="row wrap">
            <button
              className="btn btn--danger btn--sm"
              disabled={remove.isPending}
              onClick={() => {
                setReauth(null);
                remove.mutate(m.id, {
                  onSuccess: () => toast(`Removed ${label}`),
                  onError: (err) => (isReauthRequired(err) ? setReauth(err.message) : toast(err.message, { tone: 'error' })),
                });
              }}
            >
              <Trash2 size={15} /> Yes, remove
            </button>
            {/* Focus lands on the safe choice; the destructive one is a deliberate second step. */}
            <button className="btn btn--ghost btn--sm" autoFocus onClick={cancel}>
              Cancel
            </button>
          </div>
          {reauth && <ReauthPrompt message={reauth} />}
        </div>
      )}
    </div>
  );
}

/** Everyone's AI requests today against the server-wide cap (AI_DAILY_LIMIT). Members never see these numbers. */
function AiTodayLine({ used, limit, resets }: AdminView['aiToday'] & { resets: string }) {
  if (limit === 0) return <p className="small">AI is off for everyone: AI_DAILY_LIMIT is 0.</p>;
  const reached = used >= limit;
  return (
    <div className="stack" style={{ gap: 6 }}>
      <p className="small">
        Everyone today: <b className="num">{used.toLocaleString('en-US')}</b> of <b className="num">{limit.toLocaleString('en-US')}</b> AI{' '}
        {limit === 1 ? 'request' : 'requests'}.
        {reached && <> The server-wide limit is reached, so AI is paused for everyone until it resets {resets}.</>}
      </p>
      <div className="bar" style={{ maxWidth: 360 }} aria-hidden="true">
        <div className="bar__fill" style={{ width: `${Math.min(100, (used / limit) * 100)}%`, background: reached ? 'var(--bad)' : 'var(--ai)' }} />
      </div>
    </div>
  );
}

function MembersSection({ view, myId }: { view: AdminView; myId: string | undefined }) {
  // In this admin's own time zone; the counting day is still UTC's.
  const timeZone = useSettings().data?.profile.timezone ?? browserTimeZone();
  const resets = resetPhraseWithUTC(nextAIReset(), timeZone);
  return (
    <section className="card card--pad stack" style={{ gap: 4 }}>
      <div className="stack" style={{ gap: 10 }}>
        <div>
          <h2 className="section-title">Members · {view.members.length}</h2>
          <p className="small muted" style={{ marginTop: 4 }}>
            Members get {view.defaultAiLimit} AI requests a day unless you set a limit for them. Admins have no personal cap unless you set one. Counts reset {resets}.
          </p>
        </div>
        <AiTodayLine {...view.aiToday} resets={resets} />
      </div>
      <div>
        {view.members.map((m) => (
          <MemberRow key={m.id} member={m} defaultAiLimit={view.defaultAiLimit} isMe={m.id === myId} />
        ))}
      </div>
    </section>
  );
}

/** Invites, members and AI allowances. Routed only for admins with sign-in on. */
export function AdminPage() {
  const session = useSession();
  const admin = useAdmin();

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Admin</h1>
          <p className="page__sub">Invites, members and AI allowances.</p>
        </div>
      </div>
      {admin.data ? (
        <>
          <InviteSection view={admin.data} google={Boolean(session.data?.google)} emailCodes={session.data?.emailCodes ?? false} />
          <MembersSection view={admin.data} myId={session.data?.user?.id} />
        </>
      ) : admin.isError ? (
        <section className="card card--pad stack" role="alert" style={{ gap: 12 }}>
          <p className="small muted">{admin.error.message}</p>
          <button className="btn btn--primary" style={{ alignSelf: 'flex-start' }} onClick={() => void admin.refetch()}>
            Try again
          </button>
        </section>
      ) : (
        <div className="skeleton" style={{ height: 320, borderRadius: 20 }} />
      )}
    </div>
  );
}
